import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  User, Lock, Star, CreditCard, Heart, LogOut, FileText, Shield,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { formatPhoneDisplay } from '../../services/authService';
import PersonalInfoSection from '../../components/Profile/PersonalInfoSection';
import SecuritySection from '../../components/Profile/SecuritySection';
import MyReviewsSection from '../../components/Profile/MyReviewsSection';
import PaymentMethodsSection from '../../components/Profile/PaymentMethodsSection';
import './ProfilePage.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeSection, setActiveSection] = useState('personal');

  const firstName = user?.first_name || user?.firstName || '';
  const lastName = user?.last_name || user?.lastName || '';
  const email = user?.email || '';
  const phone = user?.phone || '';
  const createdAt = user?.created_at || user?.createdAt;

  const fullName = useMemo(
    () => [firstName, lastName].filter(Boolean).join(' ') || 'Utilisateur',
    [firstName, lastName]
  );

  const initials = useMemo(() => {
    const a = firstName.charAt(0);
    const b = lastName.charAt(0);
    return (`${a}${b}`.toUpperCase() || fullName.charAt(0).toUpperCase() || 'U');
  }, [firstName, lastName, fullName]);

  const memberSince = useMemo(() => {
    if (!createdAt) return '';
    return new Date(createdAt).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
    });
  }, [createdAt]);

  const menuItems = [
    { id: 'personal', icon: User, label: 'Informations personnelles' },
    { id: 'security', icon: Lock, label: 'Sécurité' },
    { id: 'reviews', icon: Star, label: 'Mes avis' },
    { id: 'payment', icon: CreditCard, label: 'Moyens de paiement' },
  ];

  const handleLogout = () => {
    if (window.confirm('Voulez-vous vous déconnecter ?')) {
      logout();
      navigate('/');
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <aside className="profile-sidebar">
          <div className="user-header">
            <div className="avatar">{initials}</div>
            <h3 className="user-name">{fullName}</h3>
            {phone && (
              <p className="user-phone">{formatPhoneDisplay(phone)}</p>
            )}
            {email && <p className="user-email">{email}</p>}
            {memberSince && (
              <p className="member-since">Membre depuis {memberSince}</p>
            )}
          </div>

          <nav className="profile-nav">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`nav-item ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(item.id)}
                >
                  <IconComponent className="nav-icon" size={18} />
                  <span className="nav-label">{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="profile-side-links">
            <Link to="/favoris" className="side-link">
              <Heart size={16} />
              Mes favoris
            </Link>
            <Link to="/cgu" className="side-link">
              <FileText size={16} />
              Conditions d’utilisation
            </Link>
            <Link to="/confidentialite" className="side-link">
              <Shield size={16} />
              Confidentialité
            </Link>
            <button type="button" className="side-link logout" onClick={handleLogout}>
              <LogOut size={16} />
              Déconnexion
            </button>
          </div>
        </aside>

        <main className="profile-main">
          {activeSection === 'personal' && <PersonalInfoSection />}
          {activeSection === 'security' && <SecuritySection />}
          {activeSection === 'reviews' && <MyReviewsSection />}
          {activeSection === 'payment' && <PaymentMethodsSection />}
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;
